import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Backdrop, Button, CircularProgress, IconButton } from '@mui/material'
import { Cameraswitch, Close, Replay } from '@mui/icons-material'
import { Html5Qrcode } from 'html5-qrcode'

function aguardar(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Área de leitura proporcional ao preview em vez de fixa em 260px.
 * Com valor fixo, os cantos do alvo estouravam o vídeo em telas estreitas.
 */
function calcularQrbox(larguraVisor, alturaVisor) {
	const menorLado = Math.min(larguraVisor, alturaVisor)
	const tamanho = Math.max(160, Math.floor(menorLado * 0.7))

	return { width: tamanho, height: tamanho }
}

const CONFIG_LEITURA = {
	fps: 10,
	qrbox: calcularQrbox
}

function escolherCameraTraseira(cameras = []) {
	if (!cameras.length) return null
	if (cameras.length === 1) return cameras[0].id

	const porLabel = (regex) => cameras.find((camera) => regex.test(camera.label || ''))

	const traseira = porLabel(/back|rear|traseir|environment|world|wide|trás/i)
	if (traseira) return traseira.id

	const naoFrontal = cameras.find((camera) => !/front|user|selfie|facial|face|frontal/i.test(camera.label || ''))
	if (naoFrontal) return naoFrontal.id

	return cameras[cameras.length - 1].id
}

async function listarCamerasComRetry(tentativas = 3) {
	let ultimoErro = null

	for (let i = 0; i < tentativas; i++) {
		try {
			const lista = await Html5Qrcode.getCameras()
			if (lista?.length) return lista
		} catch (err) {
			ultimoErro = err
		}

		if (i < tentativas - 1) await aguardar(350)
	}

	if (ultimoErro) throw ultimoErro
	return []
}

function limparContainerScanner(elementId) {
	const el = document.getElementById(elementId)
	if (el) el.innerHTML = ''
}

export default function ScannerLeituraModal({
	open,
	onClose,
	onCodigoLido,
	titulo = 'Ler código QR / barras',
	mensagemRodape = 'Aponte para o código com a câmera traseira.',
	elementId = 'scanner-leitura-reader',
	zIndex = 12000,
	processando = false,
	mensagemProcessando = 'Processando...',
	bloquearReleitura = true,
	leituraPausada = false,
	erroLeitura = '',
	onRetomarLeitura,
}) {
	const [erro, setErro] = useState('')
	const [iniciando, setIniciando] = useState(false)
	const [cameras, setCameras] = useState([])
	const [cameraId, setCameraId] = useState(null)
	const scannerRef = useRef(null)
	const camerasRef = useRef([])
	const ultimoCodigoRef = useRef('')
	const processandoRef = useRef(false)
	const leituraPausadaRef = useRef(false)
	const sessaoRef = useRef(0)

	useEffect(() => {
		processandoRef.current = processando
	}, [processando])

	useEffect(() => {
		leituraPausadaRef.current = leituraPausada
	}, [leituraPausada])

	const pararScanner = useCallback(async () => {
		const scanner = scannerRef.current
		scannerRef.current = null
		if (scanner) {
			try {
				if (scanner.isScanning) await scanner.stop()
				scanner.clear()
			} catch {
				// ignore cleanup errors
			}
		}
		limparContainerScanner(elementId)
		await aguardar(150)
	}, [elementId])

	const handleCodigo = useCallback((codigo) => {
		const valor = codigo?.trim()
		if (!valor || processandoRef.current || leituraPausadaRef.current) return
		if (bloquearReleitura && ultimoCodigoRef.current === valor) return

		ultimoCodigoRef.current = valor
		onCodigoLido?.(valor)
	}, [bloquearReleitura, onCodigoLido])

	useEffect(() => {
		if (!open) {
			sessaoRef.current += 1
			pararScanner()
			setErro('')
			setIniciando(false)
			setCameraId(null)
			ultimoCodigoRef.current = ''
			return
		}

		const sessao = ++sessaoRef.current
		let cancelado = false

		const prepararCameras = async () => {
			setIniciando(true)
			setErro('')

			await pararScanner()
			if (cancelado || sessao !== sessaoRef.current) return

			try {
				const devices = await listarCamerasComRetry()
				if (cancelado || sessao !== sessaoRef.current) return

				camerasRef.current = devices
				setCameras(devices)
				setCameraId(escolherCameraTraseira(devices))
			} catch {
				if (cancelado || sessao !== sessaoRef.current) return

				if (camerasRef.current.length) {
					setCameras(camerasRef.current)
					setCameraId(escolherCameraTraseira(camerasRef.current))
				} else {
					setCameras([])
					setCameraId('environment')
				}
			} finally {
				if (!cancelado && sessao === sessaoRef.current) {
					setIniciando(false)
				}
			}
		}

		prepararCameras()

		return () => {
			cancelado = true
		}
	}, [open, pararScanner])

	useEffect(() => {
		if (!open || !cameraId || leituraPausada) {
			if (leituraPausada) pararScanner()
			return
		}

		let cancelado = false

		const iniciar = async () => {
			setIniciando(true)
			setErro('')

			try {
				await pararScanner()
				if (cancelado) return

				limparContainerScanner(elementId)

				const scanner = new Html5Qrcode(elementId, { verbose: false })
				scannerRef.current = scanner

				const configCamera = typeof cameraId === 'string' && cameraId !== 'environment'
					? cameraId
					: { facingMode: { exact: 'environment' } }

				await scanner.start(
					configCamera,
					CONFIG_LEITURA,
					(decodedText) => handleCodigo(decodedText),
					() => {}
				)
			} catch (e) {
				if (cancelado) return

				if (cameraId !== 'environment' && typeof cameraId === 'string') {
					try {
						await pararScanner()
						if (cancelado) return

						limparContainerScanner(elementId)

						const scanner = new Html5Qrcode(elementId, { verbose: false })
						scannerRef.current = scanner
						await scanner.start(
							{ facingMode: 'environment' },
							CONFIG_LEITURA,
							(decodedText) => handleCodigo(decodedText),
							() => {}
						)
						return
					} catch {
						// segue para mensagem de erro abaixo
					}
				}

				setErro(
					e?.name === 'NotAllowedError'
						? 'Permissão de câmera negada. Habilite a câmera nas configurações do app.'
						: 'Não foi possível iniciar a câmera traseira. Verifique as permissões e tente novamente.'
				)
			} finally {
				if (!cancelado) setIniciando(false)
			}
		}

		const timer = setTimeout(iniciar, 200)

		return () => {
			cancelado = true
			clearTimeout(timer)
			pararScanner()
		}
	}, [open, cameraId, handleCodigo, pararScanner, elementId, leituraPausada])

	const trocarCamera = () => {
		const lista = camerasRef.current.length ? camerasRef.current : cameras
		if (lista.length < 2 || processando || iniciando || leituraPausada) return

		const indiceAtual = lista.findIndex((camera) => camera.id === cameraId)
		const proximoIndice = indiceAtual >= 0 ? (indiceAtual + 1) % lista.length : 0
		ultimoCodigoRef.current = ''
		setCameraId(lista[proximoIndice].id)
	}

	const handleFechar = async () => {
		await pararScanner()
		onClose?.()
	}

	const handleRetomar = () => {
		ultimoCodigoRef.current = ''
		onRetomarLeitura?.()
	}

	const listaCameras = camerasRef.current.length ? camerasRef.current : cameras
	const podeTrocarCamera = listaCameras.length > 1
	const ocupado = processando || iniciando
	const mensagemErro = erroLeitura || erro

	/**
	 * Portal no body de propósito: as telas que abrem o scanner ficam dentro de um
	 * motion.div, e a animação do framer-motion aplica transform no elemento — o que
	 * torna o ancestral o bloco de contenção do position: fixed do Backdrop. Renderizado
	 * na árvore da tela, o modal era recortado pela caixa animada em vez de ocupar a
	 * viewport, e o cabeçalho (com o botão de fechar) sobrava acima da área visível.
	 */
	return createPortal(
		<Backdrop open={open} sx={{ zIndex, alignItems: 'center', justifyContent: 'center' }}>
			<div
				className="mx-3 bg-black text-white rounded-lg overflow-hidden flex flex-col"
				style={{ width: 'min(100%, 420px)', maxHeight: 'calc(100vh - 1.5rem)' }}
			>
				<div className="flex items-center justify-between px-3 py-2 border-b border-gray-700 flex-shrink-0">
					<span className="font-medium">{titulo}</span>
					<div className="flex items-center gap-1">
						{podeTrocarCamera && (
							<IconButton
								onClick={trocarCamera}
								color="inherit"
								size="small"
								disabled={ocupado || leituraPausada}
								title="Trocar câmera"
							>
								<Cameraswitch />
							</IconButton>
						)}
						<IconButton onClick={handleFechar} color="inherit" size="small" disabled={processando}>
							<Close />
						</IconButton>
					</div>
				</div>

				{/* Encolhe e recorta o preview quando falta altura: perder faixa de imagem é
				    aceitável, empurrar o cabeçalho para fora da tela não. */}
				<div
					className="relative bg-black flex items-center justify-center"
					style={{ flex: '1 1 auto', minHeight: '160px', overflow: 'hidden' }}
				>
					<div id={elementId} className="w-full" />
					{(ocupado || leituraPausada) && (
						<div
							className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70"
							style={{ zIndex: 2 }}
						>
							{ocupado && !leituraPausada && (
								<>
									<CircularProgress color="inherit" size={36} />
									<span className="text-sm">{processando ? mensagemProcessando : 'Iniciando câmera...'}</span>
								</>
							)}
							{leituraPausada && !processando && (
								<span className="text-sm px-4 text-center text-gray-300">
									Leitura pausada. Toque em &quot;Tentar novamente&quot; para escanear outro código.
								</span>
							)}
						</div>
					)}
				</div>

				<div className="flex-shrink-0">
					{mensagemErro && (
						<Alert severity="error" className="m-2">
							{mensagemErro}
						</Alert>
					)}

					<div className="p-3">
						<p className="text-center text-sm text-gray-300 mb-3">
							{leituraPausada
								? 'Verifique o código e tente novamente quando estiver pronto.'
								: mensagemRodape}
						</p>
						{leituraPausada && onRetomarLeitura && (
							<Button
								variant="contained"
								color="primary"
								fullWidth
								className="mb-2"
								startIcon={<Replay />}
								onClick={handleRetomar}
								disabled={processando}
							>
								Tentar novamente
							</Button>
						)}
						<Button variant="outlined" color="inherit" fullWidth onClick={handleFechar} disabled={processando}>
							Fechar
						</Button>
					</div>
				</div>
			</div>
		</Backdrop>,
		document.body
	)
}
